import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { isAppNavigationActive } from '@/lib/app-navigation'
import { usePageTrailValue } from '@/hooks/usePageTrail'
import { useTranslation } from 'react-i18next'
import {
  CalendarDays,
  MoreHorizontal,
  Plus,
  Handshake,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalTitle,
  ModalCloseButton,
} from '@/components/ui/modal'
import { Badge } from '@/components/ui/badge'
import { useUnread } from '@/hooks/useUnread'
import { useCreatableObjects } from '@/hooks/useCreatableObjects'
import { useOnScreenKeyboard } from '@/hooks/useOnScreenKeyboard'
import { useBottomChromeOccupancy } from '@/hooks/useBottomChromeOccupancy'
import { formatBadgeCount } from '@/lib/badge-count'
import { useAppNavigation } from '@/hooks/useAppNavigation'

interface DrawerItem {
  to: string
  icon: LucideIcon
  label: string
  badge?: number
  /** Shown before the tap — an upgrade is sold by showing it, not hiding it. */
  tag?: string
}

// Content-sized slots preserve the same destination names as the sidebar.
// The DS2 type and touch floor stay in nav-slot. Labels may wrap at text zoom
// rather than being shortened or clipped. Order and destinations are stable.
const tabBase =
  'flex flex-1 basis-[min-content] flex-col items-center gap-0.5 rounded-[var(--radius-xl)] py-1.5 text-small font-semibold transition-[color,background-color,transform] duration-[var(--duration-fast)] active:scale-95 focus-ring nav-slot'

function tabClass(isActive: boolean) {
  return cn(
    tabBase,
    /* Parley's dock: the current destination turns cream on the dark plum. */
    isActive
      ? 'bg-[var(--rail-tile-active-bg)] text-[var(--rail-tile-active-fg)]'
      : 'text-[var(--rail-tile-fg)] hover:text-[var(--rail-text)]',
  )
}

/** Count badge — a NUMBER, never a dot. See the badge rule below. */
function CountBadge({ n }: { n: number }) {
  return (
    <span
      aria-hidden="true"
      className="absolute -top-1.5 -right-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--rail-badge-bg)] px-1 text-micro font-semibold leading-none text-[var(--rail-badge-text)]"
    >
      {formatBadgeCount(n)}
    </span>
  )
}

/**
 * The mobile bottom navigation — round 22, frames `22b` (the nav) and `22d`
 * (the mobile law). Four defects `22b` names, all closed here.
 *
 * 1. TWO DIFFERENT BARS. `isVenueOrOrganizer` used to swap the first two tabs,
 *    the create target AND a sheet entry: one app, two muscle memories. The bar
 *    is now fixed — Events, Kalender — and role only reorders the sheet.
 *
 * 2. "OPPRETT" CREATED TWO DIFFERENT OBJECTS. Same word, same icon, either
 *    `/create-event` or `/offers/create`, decided by a flag the user never set.
 *    Now the button names its object when there is one, and opens a two-choice
 *    sheet when there are two. Never a guess. The signal is
 *    `profiles.account_type`, which the user chooses in settings, not a boolean
 *    derived from `profiles.role` that cannot express «Begge» at all.
 *
 * 3. SAME DESTINATION NAMES. Content determines slot width. Small screens
 *    retain Arrangementer instead of introducing a second name for Events.
 *
 * 4. TWO BADGE LANGUAGES. The bar showed an 8px dot with no number while the
 *    sheet showed «9+». Numbers wherever a number exists; the dot survives only
 *    for "something new" with no count, which is nowhere in this bar.
 */
export function BottomNav() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const trailRoot = usePageTrailValue()[0]?.to
  const keyboardOpen = useOnScreenKeyboard()
  // The app nav is bottom chrome too. `--app-nav-inset` is declared on the layout
  // element, and the Toaster is mounted at the app root outside it, so on every
  // mobile page toasts sat 16px from the bottom over this 65px bar (measured
  // 22.09.2026 on /dashboard, /events, /destinations). Reporting here reaches the
  // root, where everything floating reads `--bottom-chrome-inset`.
  const navRef = useRef<HTMLElement | null>(null)
  useBottomChromeOccupancy(navRef, 'fixed', [], !keyboardOpen)
  /* The room the content column reserves is the dock as RENDERED, gap and safe area included: the
     token guessed 84px while the dock drew 101px, and 155px where the labels wrap on a 360px phone,
     so the chat composer sat under it (running review 25.10.2026). The token stays the first-paint
     fallback. */
  useEffect(() => {
    const el = navRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const root = document.documentElement
    const publish = () => {
      const rect = el.getBoundingClientRect()
      if (rect.height === 0) root.style.removeProperty('--bottom-nav-measured')
      else root.style.setProperty('--bottom-nav-measured', `${Math.ceil(rect.height + parseFloat(getComputedStyle(el).bottom || '0') + parseFloat(getComputedStyle(el).marginBottom || '0'))}px`)
    }
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    window.addEventListener('resize', publish)
    return () => { ro.disconnect(); window.removeEventListener('resize', publish); root.style.removeProperty('--bottom-nav-measured') }
  }, [])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  // Samme tall som sidemenyen leser — én kilde, se `hooks/useUnread.ts`.
  const { notifications: unreadCount, messages: totalUnread } = useUnread()
  // Se `useIsOperator`: Tilbud, Kommunikasjon og Katalogen er arrangørflater,
  // og arket her er den ENESTE navigasjonen under `lg` — så det er her de
  // faktisk må forsvinne for en deltaker. `useAppNavigation` holds that rule.

  const { canCreate, loading: offersLoading } = useCreatableObjects()
  const bothObjects = canCreate.length > 1
  const onlyOffer = canCreate.length === 1 && canCreate[0] === 'offer'

  const entries = useAppNavigation('mobile')
  const itemFor = (entry: (typeof entries)[number], short = false): DrawerItem => ({
    to: entry.href,
    icon: entry.icon,
    label: t(short ? (entry.shortLabelKey ?? entry.labelKey) : entry.labelKey),
    badge:
      entry.badgeSource === 'messages'
        ? totalUnread
        : entry.badgeSource === 'notifications'
          ? unreadCount
          : undefined,
    tag: entry.tag,
  })
  const leadItems = entries
    .filter((entry) => entry.key === 'events' || entry.key === 'calendar')
    .map((entry) => itemFor(entry, true))

  // Single-object accounts get a button that says what it makes; dual-object
  // accounts get a chooser, which is unambiguous for a different reason.
  const createLabel = bothObjects
    ? t('nav.create')
    : onlyOffer
      ? t('nav.newOfferShort')
      : t('nav.newEventShort')
  const createAria = bothObjects
    ? t('nav.createChoose')
    : onlyOffer
      ? t('offers.newOffer')
      : t('nav.createEvent')

  const messagesItem = itemFor(entries.find(entry => entry.key === 'messages')!)
  const sellsServices = canCreate.includes('offer')
  const drawerKeys = new Set(['events', 'calendar', 'messages'])
  const drawerItems = entries
    .filter((entry) => !drawerKeys.has(entry.key))
    .sort((a, b) => {
      if (!sellsServices) return 0
      if (a.key === 'offers' && b.key === 'dashboard') return -1
      if (a.key === 'dashboard' && b.key === 'offers') return 1
      return 0
    })
    .map((entry) => itemFor(entry))
  // «Mer» is where the current page lives when it is one of the sheet's destinations: lit, so
  // the dock never shows no position at all on /dashboard, /offers or /notifications (30.10.2026).
  const moreActive = drawerItems.some((item) => isAppNavigationActive(pathname, item.to, trailRoot))

  return (
    <>
      <nav
        ref={navRef}
        aria-label={t('nav.mainNav')}
        /* Hidden while the on-screen keyboard is up. With
           `interactive-widget=resizes-content` a fixed bottom bar re-pins ON
           TOP of the keyboard, in the strip between the field and the keys —
           which no native app does, and which spends 64px of the little room
           left for what is being typed. `translate-y-full` rather than
           `hidden` so it slides rather than blinks, and so it keeps its box in
           the layout for the inset the content column reserves.
           `aria-hidden` + `pointer-events-none` so it leaves the a11y tree
           while off-screen instead of being a set of invisible targets. */
        className={cn(
          /* Parley (24.10.2026): a floating dark dock with the page's gap around it, not a
             full-width bar. `--bottom-nav-base-height` includes the gap, so every inset that
             reserves the bar's room still adds up. */
          'fixed bottom-[var(--rail-gap)] left-[var(--rail-gap)] right-[var(--rail-gap)] z-[var(--z-nav)] rounded-[var(--card-radius)] bg-[var(--rail-bg)] shadow-[var(--shadow-lg)] lg:hidden',
          'transition-transform duration-[var(--duration-base)] ease-[var(--ease-out)]',
          keyboardOpen && 'pointer-events-none translate-y-full',
        )}
        aria-hidden={keyboardOpen || undefined}
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex flex-wrap min-h-16 items-center justify-around p-1.5">
          {leadItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              aria-current={isAppNavigationActive(pathname, item.to, trailRoot) ? 'page' : undefined}
              className={tabClass(isAppNavigationActive(pathname, item.to, trailRoot))}
            >
              <item.icon className="size-5" aria-hidden="true" />
              <span className="max-w-full whitespace-normal text-center [overflow-wrap:normal]">{item.label}</span>
            </Link>
          ))}

          {/* Create — the primary action, always accented, always named */}
          {bothObjects ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              aria-label={createAria}
              aria-haspopup="dialog"
              className={cn(tabBase, 'text-[var(--rail-text)]')}
            >
              <span aria-hidden="true" className="flex h-6 w-9 items-center justify-center rounded-[var(--radius-md)] bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)]"><Plus className="size-4" strokeWidth={2.6} /></span>
              <span className="max-w-full whitespace-normal text-center [overflow-wrap:normal] min-[360px]:whitespace-nowrap">{createLabel}</span>
            </button>
          ) : (
            <Link
              to={onlyOffer ? '/offers/create' : '/create-event'}
              aria-label={createAria}
              // Until the organisation's modules are known the destination may change, so the
              // link waits rather than open the wrong creation flow (review 22.10.2026).
              aria-disabled={offersLoading || undefined}
              tabIndex={offersLoading ? -1 : undefined}
              onClick={(event) => { if (offersLoading) event.preventDefault() }}
              className={cn(tabBase, 'text-[var(--rail-text)]', offersLoading && 'pointer-events-none opacity-50')}
            >
              <span aria-hidden="true" className="flex h-6 w-9 items-center justify-center rounded-[var(--radius-md)] bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)]"><Plus className="size-4" strokeWidth={2.6} /></span>
              <span className="max-w-full whitespace-normal text-center [overflow-wrap:normal] min-[360px]:whitespace-nowrap">{createLabel}</span>
            </Link>
          )}

          {/* Messages — a number, not a dot */}
          <NavLink to={messagesItem.to} className={({ isActive }) => tabClass(isActive)}>
            <span className="relative">
              <messagesItem.icon className="size-5" aria-hidden="true" />
              {totalUnread > 0 && <CountBadge n={totalUnread} />}
            </span>
            <span className="max-w-full whitespace-normal text-center [overflow-wrap:normal]">{messagesItem.label}</span>
            {totalUnread > 0 && (
              <span className="sr-only">
                ({totalUnread} {t('nav.unread')})
              </span>
            )}
          </NavLink>

          {/* More — opens the accessible DS bottom-sheet */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label={t('nav.more')}
            aria-haspopup="dialog"
            className={tabClass(moreActive)}
          >
            <span className="relative">
              <MoreHorizontal className="size-5" aria-hidden="true" />
              {unreadCount > 0 && <CountBadge n={unreadCount} />}
            </span>
            <span className="max-w-full whitespace-normal text-center [overflow-wrap:normal]">{t('nav.more')}</span>
          </button>
        </div>
      </nav>

      {/* "Create" chooser — only ever reached by an account that can make both.
          `22b`: «for de som kan begge åpner den et lite ark med to valg. Aldri
          et gjettespill.» */}
      <Modal open={createOpen} onOpenChange={setCreateOpen}>
        <ModalContent mobileBottomSheet size="sm">
          <ModalHeader>
            <ModalTitle>{t('nav.createChoose')}</ModalTitle>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody look="sheet">
            <ul className="flex flex-col gap-1">
              {[
                { to: '/create-event', icon: CalendarDays, label: t('nav.createEvent') },
                { to: '/offers/create', icon: Handshake, label: t('offers.newOffer') },
              ].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => setCreateOpen(false)}
                    className="flex min-h-14 w-full items-center gap-3 rounded-[var(--radius-lg)] px-4 text-label text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)] focus-ring"
                  >
                    <item.icon className="size-5 shrink-0" aria-hidden="true" />
                    <span className="flex-1">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* "More" sheet — DS Modal gives a focus-trap, scroll-lock, backdrop +
          Escape for free (replacing the former hand-rolled overlay with none of
          those). */}
      <Modal open={drawerOpen} onOpenChange={setDrawerOpen}>
        <ModalContent mobileBottomSheet size="sm">
          <ModalHeader>
            <ModalTitle>{t('nav.more')}</ModalTitle>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody look="sheet">
            <ul className="flex flex-col gap-1">
              {drawerItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => setDrawerOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-14 w-full items-center gap-3 rounded-[var(--radius-lg)] px-4 text-label transition-colors focus-ring',
                        isActive
                          ? 'bg-[var(--color-interactive-primary-bg)] font-medium text-[var(--color-interactive-primary)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)]',
                      )
                    }
                  >
                    <span className="relative shrink-0">
                      <item.icon className="size-5" aria-hidden="true" />
                      {item.badge != null && item.badge > 0 && <CountBadge n={item.badge} />}
                    </span>
                    <span className="flex-1">{item.label}</span>
                    {item.tag && (
                      <Badge variant="neutral" size="sm" aria-hidden="true">
                        {item.tag}
                      </Badge>
                    )}
                    {item.tag && <span className="sr-only"> – {item.tag}</span>}
                    {item.badge != null && item.badge > 0 && (
                      <span className="sr-only">
                        ({item.badge} {t('nav.unread')})
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
